import * as m from '@staiter/i18n';
import type { WoCreateInput } from '@staiter/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import { Controller } from 'react-hook-form';
import { z } from 'zod';
import { PageHeader, Table } from '~/components/data';
import { BomPicker, ItemPicker, UserPicker, WarehousePicker } from '~/components/domain';
import { useToast } from '~/components/feedback';
import { Button, Checkbox, DateTimePicker, Input, Textarea } from '~/components/primitives';
import { applyValidationErrors } from '~/lib/api/apply-validation-errors';
import { createWorkOrder, fetchManufacturePreview } from '~/lib/api/work-orders';
import { useZodForm } from '~/lib/forms/useZodForm';
import shared from './_auth._shared.module.css';
import styles from './_auth.work-orders.new.module.css';

export const Route = createFileRoute('/_auth/work-orders/new')({ component: NewWorkOrder });

/**
 * F14 — локальная schema формы WO/new. WoCreateInput требует voucher_no:min(1),
 * но UI позволяет оставить пустым (backend генерит). Храним voucher_no_optional,
 * а на submit мапим в WoCreateInput. `qty` — обязательное decimal-поле UI.
 */
const DecimalPos = z
  .string()
  .min(1, m.route_wo_new_err_qty_required())
  .regex(/^\d+(\.\d+)?$/, m.route_wo_new_err_qty_format())
  .refine((v) => Number(v) > 0, m.route_wo_new_err_qty_positive());

/**
 * U3.1 / U3.2 / U3.3 / U3.4 / U16.2 — Sprint B.
 * - убран дубль `qty` (оставили `qty_to_produce` → mapping)
 * - `notes` убран из payload (не было UI)
 * - `voucher_no` снят из формы целиком — backend W2 генерит автоматически
 * - добавлены `source_warehouse_id` + `scrap_warehouse_id` pickers
 * - refine `planned_end_at > planned_start_at`
 */
const FormSchema = z
  .object({
    item_id: z.string().uuid(m.route_wo_new_err_item_required()),
    bom_id: z.string().uuid(m.route_wo_new_err_bom_required()),
    qty: DecimalPos,
    planned_start_at: z.string().nullable().default(null),
    planned_end_at: z.string().nullable().default(null),
    executor_id: z.string().nullable().default(null),
    source_warehouse_id: z.string().nullable().default(null),
    wip_warehouse_id: z.string().nullable().default(null),
    fg_warehouse_id: z.string().nullable().default(null),
    scrap_warehouse_id: z.string().nullable().default(null),
    /** U3.2 — опциональные примечания. Backend принимает `notes` в WoCreateInput. */
    notes: z.string().max(2000).default(''),
  })
  .refine(
    (d) => !d.planned_start_at || !d.planned_end_at || d.planned_end_at > d.planned_start_at,
    { message: m.route_wo_new_err_end_after_start(), path: ['planned_end_at'] },
  );
type FormValues = z.infer<typeof FormSchema>;

function NewWorkOrder(): JSX.Element {
  const nav = useNavigate();
  const toast = useToast();
  const [createdId, setCreatedId] = useState<string | null>(null);

  // FE-109 Sprint Q+: smart defaults — start today, end +3 days (typical small WO).
  // FIX (2026-06-05): slice(0,16) обрезал секунды + Z → BE z.string().datetime()
  // отвергал значения как malformed. Передаём full ISO без trim — DateTimePicker
  // умеет показывать в формате «DD.MM.YYYY HH:MM», не трогая wire format.
  const now = new Date();
  const todayIso = now.toISOString();
  const threeDaysLater = new Date(now.getTime() + 3 * 86400_000).toISOString();
  const form = useZodForm(
    FormSchema,
    {
      item_id: '',
      bom_id: '',
      qty: '1',
      planned_start_at: todayIso,
      planned_end_at: threeDaysLater,
      executor_id: null,
      source_warehouse_id: null,
      wip_warehouse_id: null,
      fg_warehouse_id: null,
      scrap_warehouse_id: null,
      notes: '',
    },
    // FIX 2026-06-05 — onChange чтобы isValid пересчитывался сразу при выборе
    // item/BOM в pickers, не ждал blur. Иначе submit-button остаётся disabled
    // даже когда форма уже валидна (пользователь не понимает, почему).
    { mode: 'onChange' },
  );
  const { control, handleSubmit, register, formState } = form;
  const { errors, isValid } = formState;

  // U16.3 — «Остаться на странице» чекбокс (для создания нескольких WO подряд).
  const [stayOnPage, setStayOnPage] = useState(false);

  const createMut = useMutation({
    mutationFn: (input: WoCreateInput) => createWorkOrder(input),
    onSuccess: (wo) => {
      toast.push({ variant: 'success', title: m.route_wo_new_toast_created({ code: wo.code }) });
      setCreatedId(wo.id);
      if (!stayOnPage) {
        void nav({ to: '/work-orders/$id', params: { id: wo.id } });
      }
    },
    onError: (err: Error) => {
      // S-FIX-9-FE §4 — inline field errors из BE ApiErrorStrict envelope.
      // FIX 2026-06-05 — BE использует UI-alias имена (qty_to_produce / planned_start),
      // FE form имеет (qty / planned_start_at). Маппинг чтобы setError попал в RHF.
      const pathMap: Record<string, string> = {
        qty_to_produce: 'qty',
        planned_start: 'planned_start_at',
        planned_end: 'planned_end_at',
        remarks: 'notes',
      };
      if (applyValidationErrors(form, err, pathMap)) return;
      toast.push({
        variant: 'error',
        title: m.route_wo_new_toast_create_failed(),
        description: err.message,
      });
    },
  });

  const { data: preview } = useQuery({
    queryKey: ['wo-preview', createdId],
    queryFn: () => fetchManufacturePreview(createdId as string),
    enabled: !!createdId,
  });

  const onSubmit = (data: FormValues): void => {
    // U3.4 — voucher_no не шлём; backend W2 auto-generates.
    // U3.1 — `qty` duplicate removed; используем только `qty_to_produce`.
    // U3.2 — `notes` опционально.
    const input: WoCreateInput = {
      item_id: data.item_id,
      bom_id: data.bom_id,
      qty_to_produce: data.qty,
      planned_start_at: data.planned_start_at,
      planned_end_at: data.planned_end_at,
      executor_id: data.executor_id,
      source_warehouse_id: data.source_warehouse_id,
      wip_warehouse_id: data.wip_warehouse_id,
      fg_warehouse_id: data.fg_warehouse_id,
      scrap_warehouse_id: data.scrap_warehouse_id,
      ...(data.notes.trim() ? { notes: data.notes.trim() } : {}),
    };
    createMut.mutate(input);
  };

  return (
    <div className={shared.page}>
      <PageHeader title={m.route_wo_new_title()} description={m.route_wo_new_description()} />

      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        {/* FE-108 Sprint Q+: form sections (Product / Schedule / Warehouses / Notes). */}
        <section className={shared.formSection}>
          <h3 className={shared.formSectionHeading}>{m.wo_new_section_product()}</h3>
          <Controller
            control={control}
            name="item_id"
            render={({ field, fieldState }) => (
              <ItemPicker
                label={m.route_wo_new_item_label()}
                required
                itemType="finished_good"
                value={field.value}
                onChange={field.onChange}
                error={fieldState.error?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="bom_id"
            render={({ field, fieldState }) => (
              <BomPicker
                label={m.route_wo_new_bom_label()}
                required
                hint={m.route_wo_new_bom_hint()}
                value={field.value}
                onChange={field.onChange}
                error={fieldState.error?.message}
              />
            )}
          />
          <Input
            label={m.route_wo_new_qty_label()}
            required
            inputMode="decimal"
            {...register('qty')}
            error={errors.qty?.message}
          />
        </section>

        <section className={shared.formSection}>
          <h3 className={shared.formSectionHeading}>{m.wo_new_section_schedule()}</h3>
          <div className={styles.row}>
            <Controller
              control={control}
              name="planned_start_at"
              render={({ field, fieldState }) => (
                <DateTimePicker
                  label={m.route_wo_new_planned_start_label()}
                  value={field.value}
                  onChange={field.onChange}
                  {...(fieldState.error?.message ? { error: fieldState.error.message } : {})}
                />
              )}
            />
            <Controller
              control={control}
              name="planned_end_at"
              render={({ field, fieldState }) => (
                <DateTimePicker
                  label={m.route_wo_new_planned_end_label()}
                  value={field.value}
                  onChange={field.onChange}
                  {...(fieldState.error?.message ? { error: fieldState.error.message } : {})}
                />
              )}
            />
          </div>
          <Controller
            control={control}
            name="executor_id"
            render={({ field, fieldState }) => (
              <UserPicker
                label={m.route_wo_new_executor_label()}
                value={field.value ?? ''}
                onChange={(v) => field.onChange(v || null)}
                error={fieldState.error?.message}
              />
            )}
          />
        </section>

        <section className={shared.formSection}>
          <h3 className={shared.formSectionHeading}>{m.wo_new_section_warehouses()}</h3>
          <div className={styles.row}>
            <Controller
              control={control}
              name="source_warehouse_id"
              render={({ field, fieldState }) => (
                <WarehousePicker
                  label={m.route_wo_new_source_wh_label()}
                  hint={m.route_wo_new_source_wh_hint()}
                  value={field.value ?? ''}
                  onChange={(v) => field.onChange(v || null)}
                  error={fieldState.error?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="wip_warehouse_id"
              render={({ field, fieldState }) => (
                <WarehousePicker
                  label={m.route_wo_new_wip_wh_label()}
                  value={field.value ?? ''}
                  onChange={(v) => field.onChange(v || null)}
                  error={fieldState.error?.message}
                />
              )}
            />
          </div>

          <div className={styles.row}>
            <Controller
              control={control}
              name="fg_warehouse_id"
              render={({ field, fieldState }) => (
                <WarehousePicker
                  label={m.route_wo_new_fg_wh_label()}
                  value={field.value ?? ''}
                  onChange={(v) => field.onChange(v || null)}
                  error={fieldState.error?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="scrap_warehouse_id"
              render={({ field, fieldState }) => (
                <WarehousePicker
                  label={m.route_wo_new_scrap_wh_label()}
                  hint={m.route_wo_new_scrap_wh_hint()}
                  value={field.value ?? ''}
                  onChange={(v) => field.onChange(v || null)}
                  error={fieldState.error?.message}
                />
              )}
            />
          </div>
        </section>

        <section className={shared.formSection}>
          <h3 className={shared.formSectionHeading}>{m.wo_new_section_notes()}</h3>
          {/* FE-12 Sprint M+: raw <textarea> → Textarea primitive (focus ring + label-above). */}
          <Textarea
            {...register('notes')}
            rows={3}
            maxLength={2000}
            label={m.route_wo_new_notes_label()}
            placeholder={m.route_wo_new_notes_placeholder()}
            {...(errors.notes?.message ? { error: errors.notes.message } : {})}
          />
        </section>

        <div className={`${styles.actions} ${shared.stickyFooter}`}>
          {/* FE-110 Sprint Q+: explainer via Checkbox.description prop. */}
          <Checkbox
            checked={stayOnPage}
            onChange={(e) => setStayOnPage(e.target.checked)}
            label={m.route_wo_new_stay_on_page()}
            description={m.wo_stay_on_page_hint()}
          />
          {/* FE-106 Sprint O+: Cancel button. */}
          <Button type="button" variant="secondary" onClick={() => nav({ to: '/work-orders' })}>
            {m.common_button_cancel()}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!isValid || createMut.isPending}
            loading={createMut.isPending}
          >
            {m.route_wo_new_btn_submit()}
          </Button>
          {createdId && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => nav({ to: '/work-orders/$id', params: { id: createdId } })}
            >
              {m.route_wo_new_btn_open()}
            </Button>
          )}
        </div>
      </form>

      {preview && (
        <section className={styles.preview}>
          <h3 className={shared.sectionHeading}>{m.route_wo_new_preview_heading()}</h3>
          {!preview.can_release && (
            <div role="alert" className={styles.dangerAlert}>
              <AlertTriangle size={16} aria-hidden="true" />
              {m.route_wo_new_preview_insufficient()}
            </div>
          )}
          <Table
            ariaLabel={m.route_wo_new_preview_table_aria()}
            data={preview.requirements ?? []}
            rowKey={(r) => r.item_id}
            columns={[
              {
                id: 'item',
                header: m.route_wo_new_col_material(),
                accessor: (r) => r.item_name ?? r.item_id.slice(0, 8),
                mono: true,
              },
              {
                id: 'req',
                header: m.route_wo_new_col_required(),
                accessor: (r) => `${r.qty_required} ${r.uom}`,
                mono: true,
                align: 'right',
              },
              {
                id: 'avail',
                header: m.route_wo_new_col_available(),
                accessor: (r) => r.qty_available,
                mono: true,
                align: 'right',
              },
              {
                id: 'shortage',
                header: m.route_wo_new_col_shortage(),
                align: 'right',
                mono: true,
                accessor: (r) =>
                  Number(r.shortage) > 0 ? (
                    <span className={styles.shortageValue}>−{r.shortage}</span>
                  ) : (
                    '—'
                  ),
              },
            ]}
          />
          {preview.warnings.length > 0 && (
            <div className={styles.warningsBlock}>
              <h4>{m.route_wo_new_warnings_heading()}</h4>
              <ul>
                {preview.warnings.map((w, idx) => (
                  <li key={`${w.type}-${w.severity}-${idx}`}>
                    <strong>{m.wo_warning_type_label({ type: w.type })}</strong> ({w.severity})
                    {w.related_item_id ? ` — ${w.related_item_id}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
